import { $t } from '@/plugins/fluent';
import { DateTime, MonthNumbers } from 'luxon';
import { ConflictsByDay, ConflictStatus } from '../Timeline.types';

export type TimelineHeaderDay = {
    number: number;
    fullName: string;
    abbreviation: string;
    conflictStatus: ConflictStatus;
    isWorkDay: boolean;
    formattedDate: string;
    columnId: string;
    isFirstColumn: boolean;
};

export type TimelineHeaderCalendarWeek = {
    daysCount: number;
    label: string;
    startColumnIndex: number;
};

export type TimelineHeaderMonth = {
    daysCount: number;
    label: string;
    startColumnIndex: number;
};

export function calculateHeaderDays(
    conflictsByDay: ConflictsByDay,
    startDateTime: DateTime,
    durationInDays: number,
): TimelineHeaderDay[] {
    const daysArray: TimelineHeaderDay[] = [];

    for (let dayIndex = 0; dayIndex < durationInDays; dayIndex++) {
        const dateTime = startDateTime.plus({ days: dayIndex });

        const isoDate = dateTime.toISODate();
        if (!isoDate) {
            throw new Error('Could not convert DateTime to ISO date string');
        }
        const conflictStatus = conflictsByDay.get(isoDate) ?? 'No-Conflict';

        daysArray.push({
            number: dateTime.day,
            abbreviation: $t('day-abbreviated', { dayNumber: dateTime.weekday }),
            fullName: $t('day-full-name', { dayNumber: dateTime.weekday }),
            isWorkDay: dateTime.weekday !== 6 && dateTime.weekday !== 7,
            conflictStatus,
            formattedDate: dateTime.toLocaleString({
                ...DateTime.DATE_SHORT,
                day: '2-digit',
                month: '2-digit',
            }),
            columnId: `column-${dayIndex}`,
            isFirstColumn: dayIndex === 0,
        });
    }

    return daysArray;
}

export function calculateHeaderCalendarWeeks(
    startDateTime: DateTime,
    durationInDays: number,
): TimelineHeaderCalendarWeek[] {
    const calendarWeeksArray: TimelineHeaderCalendarWeek[] = [];
    let currentCalendarWeek = -1;

    for (let day = 0; day < durationInDays; day++) {
        const dateTime = startDateTime.plus({ days: day });

        if (currentCalendarWeek !== dateTime.weekNumber) {
            currentCalendarWeek = dateTime.weekNumber;

            calendarWeeksArray.push({
                daysCount: 1,
                label: $t('calendar-week', { calendarWeek: currentCalendarWeek }),
                startColumnIndex: day + 1,
            });
        } else {
            calendarWeeksArray[calendarWeeksArray.length - 1].daysCount += 1;
        }
    }

    return calendarWeeksArray;
}

export function calculateHeaderMonths(startDateTime: DateTime, durationInDays: number): TimelineHeaderMonth[] {
    const monthArray: TimelineHeaderMonth[] = [];
    let currentMonth: MonthNumbers | -1 = -1;

    for (let day = 0; day < durationInDays; day++) {
        const dateTime = startDateTime.plus({ days: day });

        if (currentMonth !== dateTime.month) {
            currentMonth = dateTime.month as MonthNumbers;

            monthArray.push({
                daysCount: 1,
                label: $t('month-with-year', { month: dateTime.month, yearString: String(dateTime.year) }),
                startColumnIndex: day + 1,
            });
        } else {
            monthArray[monthArray.length - 1].daysCount += 1;
        }
    }

    return monthArray;
}
